import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ChevronLeft, ChevronRight, Clock, Camera } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

export function ProjectDevelopmentUpdates() {
  const { id } = useParams<{ id: string }>();
  const [selectedIdx, setSelectedIdx] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: updates, isLoading } = useQuery({
    queryKey: ['project-updates', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_updates' as any)
        .select('*')
        .eq('project_id', id!)
        .order('update_date', { ascending: false });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!id,
  });

  if (isLoading) return <Skeleton className="h-40 rounded-xl" />;
  if (!updates || updates.length === 0) return null;

  const selected = updates[selectedIdx];
  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const formatShort = (d: string) => {
    const dt = new Date(d);
    return { month: dt.toLocaleDateString('en-US', { month: 'short' }), year: dt.getFullYear().toString() };
  };

  return (
    <section>
      <h2 className="font-display text-lg md:text-xl font-bold mb-3 md:mb-4">Development Updates</h2>
      <div className="border rounded-xl p-3 md:p-6 max-w-full overflow-hidden">
        <div className="flex items-center gap-2 mb-4 md:mb-6 max-w-full">
          <button onClick={() => scrollRef.current?.scrollBy({ left: -120, behavior: 'smooth' })} className="shrink-0 p-1 rounded-full hover:bg-muted transition-colors hidden md:block">
            <ChevronLeft className="h-4 w-4 text-muted-foreground" />
          </button>
          <div ref={scrollRef} className="flex gap-2 overflow-x-auto no-scrollbar flex-1 min-w-0">
            {updates.map((u: any, i: number) => {
              const { month, year } = formatShort(u.update_date);
              return (
                <button key={u.id} onClick={() => setSelectedIdx(i)}
                  className={`shrink-0 px-3 md:px-4 py-2 rounded-full text-xs font-medium transition-all text-center leading-tight ${i === selectedIdx ? 'bg-primary text-primary-foreground ring-2 ring-primary/30' : 'text-muted-foreground hover:bg-muted'}`}>
                  {month}<br />{year}
                </button>
              );
            })}
          </div>
          <button onClick={() => scrollRef.current?.scrollBy({ left: 120, behavior: 'smooth' })} className="shrink-0 p-1 rounded-full hover:bg-muted transition-colors hidden md:block">
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <div className="border-t pt-4">
          <span className="inline-block px-3 py-1 bg-muted rounded-full text-xs font-medium mb-3 md:mb-4">{formatDate(selected.update_date)}</span>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {selected.image_url && (
              <div className="w-full sm:w-36 aspect-video sm:aspect-auto sm:h-24 rounded-lg bg-muted shrink-0 overflow-hidden">
                <img src={selected.image_url} alt={selected.title} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold text-sm break-words">{selected.title}</h4>
              {selected.description && <p className="text-xs text-muted-foreground mt-1 break-words">{selected.description}</p>}
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
                <Clock className="h-3 w-3 shrink-0" /> <span className="truncate">Posted {formatDate(selected.update_date)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
